import { useState, useEffect } from 'react'
import api from '../api/axios'
import PlatformAnalytics from '../components/analytics/PlatformAnalytics'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell,
} from 'recharts'
import {
  ChartBarIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'

interface FeatureAdoption {
  feature_key: string
  adoption_rate: number
  total_enabled: number
  total_companies: number
}

interface SavedReport {
  id: string
  report_name: string
  report_type: string
  description: string
  created_by: string
  last_run_at: string
  export_format: string
}

type ActiveTab = 'overview' | 'features' | 'custom'

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.name ?? p.dataKey}: {p.value}</p>
      ))}
    </div>
  )
}

export default function ReportsAnalytics() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [featureAdoption, setFeatureAdoption] = useState<FeatureAdoption[]>([])
  const [savedReports, setSavedReports] = useState<SavedReport[]>([])
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview')
  const [exporting, setExporting] = useState(false)
  const [reportType, setReportType] = useState('company_analytics')
  const [dateRange, setDateRange] = useState('30d')
  const [exportFormat, setExportFormat] = useState<'csv' | 'pdf'>('csv')

  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    setLoading(true)
    setError('')
    try {
      // The Platform tab loads its own data (components/analytics/PlatformAnalytics).
      const [featuresRes, reportsRes] = await Promise.all([
        api.get('/super-admin/reports/analytics/feature-adoption'),
        api.get('/super-admin/reports/saved'),
      ])

      const adoptionData = featuresRes.data.adoption || featuresRes.data.features || []
      setFeatureAdoption(adoptionData.map((f: any) => ({
        feature_key: f.feature_key,
        adoption_rate: Number(f.adoption_rate ?? f.adoption_percentage ?? 0),
        total_enabled: Number(f.enabled_count ?? f.total_enabled ?? 0),
        total_companies: Number(f.total_companies ?? f.company_count ?? 0),
      })))

      setSavedReports(reportsRes.data.reports || [])
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  const exportReport = async (format: 'csv' | 'pdf') => {
    setExporting(true)
    try {
      const dataset = activeTab === 'features' ? featureAdoption : []
      if (dataset.length === 0) { alert('No data available to export'); setExporting(false); return }
      const response = await api.post('/super-admin/reports/export', {
        data: dataset, format,
        filename: `report_${reportType}_${dateRange}`,
        columns: Object.keys(dataset[0]),
      }, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `report_${new Date().toISOString().split('T')[0]}.${format}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch { alert('Failed to export report') } finally { setExporting(false) }
  }

  const runSavedReport = async (reportId: string) => {
    try {
      await api.post(`/super-admin/reports/execute/${reportId}`, {})
      alert('Report execution started')
      fetchData()
    } catch { alert('Failed to run report') }
  }

  const featureBarData = [...featureAdoption]
    .sort((a, b) => b.adoption_rate - a.adoption_rate)
    .slice(0, 15)
    .map(f => ({
      name: f.feature_key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      adoption: Math.round(f.adoption_rate),
      enabled: f.total_enabled,
      total: f.total_companies,
    }))

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading analytics…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ChartBarIcon className="w-7 h-7 text-blue-600" />
            Reports & Analytics
          </h1>
          <div className="flex items-center gap-2">
            <button onClick={fetchData} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
              <ArrowPathIcon className="w-4 h-4" /> Refresh
            </button>
            <button onClick={() => exportReport('csv')} disabled={exporting}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
              <ArrowDownTrayIcon className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {error && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-700">
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" /> {error}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-slate-200 -mb-px">
          {([
            { key: 'overview' as const, label: 'Platform' },
            { key: 'features' as const, label: 'Feature Adoption' },
            { key: 'custom' as const, label: 'Export Reports' },
          ] as const).map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview: platform analytics from every company's own data */}
        {activeTab === 'overview' && <PlatformAnalytics />}

        {/* Feature Adoption Tab */}
        {activeTab === 'features' && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Feature Adoption</h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  {featureAdoption.length > 0
                    ? `${featureAdoption.length} features tracked across all companies`
                    : 'No feature adoption data available'}
                </p>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" />High (&gt;75%)</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500 inline-block" />Medium</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-400 inline-block" />Low (&lt;50%)</span>
              </div>
            </div>

            {/* Summary stats */}
            {featureAdoption.length > 0 && (
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'High Adoption (>75%)', count: featureAdoption.filter(f => f.adoption_rate > 75).length, color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
                  { label: 'Medium (50–75%)', count: featureAdoption.filter(f => f.adoption_rate > 50 && f.adoption_rate <= 75).length, color: 'bg-amber-50 border-amber-200 text-amber-700' },
                  { label: 'Low (<50%)', count: featureAdoption.filter(f => f.adoption_rate <= 50).length, color: 'bg-red-50 border-red-200 text-red-700' },
                ].map(s => (
                  <div key={s.label} className={`rounded-xl border p-4 text-center ${s.color}`}>
                    <div className="text-2xl font-bold">{s.count}</div>
                    <div className="text-xs font-medium mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {featureAdoption.length > 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Top Features by Adoption Rate</h3>
                <ResponsiveContainer width="100%" height={Math.max(300, featureBarData.length * 30)}>
                  <BarChart data={featureBarData} layout="vertical" barSize={16} margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `${v}%`} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} width={150} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="adoption" name="Adoption %" radius={[0, 4, 4, 0]}>
                      {featureBarData.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={entry.adoption > 75 ? '#10b981' : entry.adoption > 50 ? '#f59e0b' : '#f87171'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
                <ChartBarIcon className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-400">No feature adoption data available</p>
              </div>
            )}
          </div>
        )}

        {/* Export Reports Tab */}
        {activeTab === 'custom' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-base font-semibold text-slate-900 mb-4">Generate Report</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Report Type</label>
                  <select value={reportType} onChange={e => setReportType(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
                    <option value="company_analytics">Company Analytics</option>
                    <option value="system_performance">System Performance</option>
                    <option value="financial">Financial Summary</option>
                    <option value="user_activity">User Activity</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Date Range</label>
                  <select value={dateRange} onChange={e => setDateRange(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                    <option value="90d">Last 90 Days</option>
                    <option value="1y">Last Year</option>
                    <option value="all">All Time</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Format</label>
                  <div className="flex gap-2">
                    {(['csv', 'pdf'] as const).map(fmt => (
                      <button key={fmt} onClick={() => setExportFormat(fmt)}
                        className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors uppercase ${exportFormat === fmt ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}>
                        {fmt}
                      </button>
                    ))}
                  </div>
                </div>
                <button onClick={() => exportReport(exportFormat)} disabled={exporting}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-50">
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  {exporting ? 'Generating…' : 'Generate & Download'}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-base font-semibold text-slate-900 mb-4">Saved Reports</h2>
              {savedReports.length === 0 ? (
                <div className="text-center py-12">
                  <ChartBarIcon className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No saved reports</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {savedReports.map(report => (
                    <div key={report.id} className="border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">{report.report_type}</span>
                        <span className="text-xs font-semibold text-slate-400">{report.export_format.toUpperCase()}</span>
                      </div>
                      <p className="text-sm font-semibold text-slate-900">{report.report_name}</p>
                      {report.description && <p className="text-xs text-slate-500 mt-0.5">{report.description}</p>}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                        <p className="text-xs text-slate-400">
                          {report.created_by}
                          {report.last_run_at && <span className="ml-2">· {new Date(report.last_run_at).toLocaleDateString()}</span>}
                        </p>
                        <button onClick={() => runSavedReport(report.id)}
                          className="px-3 py-1 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
                          Run
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
